/**
 * The app-shell auth bootstrap: who is signed in, decided before any page renders.
 *
 * On 12 Oct 2026 the single API task stalled for about three minutes before ECS
 * killed it (11:11–11:15 UTC). Every `/api/auth/csrf-token` started in that
 * window never completed, and the shell waited on it — then on `/auth/me` —
 * with only the client's 120s fetch timeout, so a reload showed a blank
 * spinner for minutes. The bootstrap now has its own short timeout, and only a
 * real 401 means "signed out": a timeout, a network error, a 5xx or an HTML
 * error page from the load balancer is an outage, shown with a Retry button.
 */

/** Long enough for a slow-but-healthy API (/auth/me runs in ~20ms), short enough to never feel stuck. */
export const BOOTSTRAP_TIMEOUT_MS = 10_000;

/** The api client throws this exact message on a 401 from an auth-check endpoint. */
const UNAUTHORIZED_MESSAGE = 'Unauthorized';

export type BootstrapFailure =
  | { kind: 'signed-out' }
  | { kind: 'unavailable'; message: string };

/** Sort a failed `/auth/me` into "signed out" (go to login) or "the API is unavailable" (show Retry). */
export function classifyBootstrapError(err: unknown): BootstrapFailure {
  if (err instanceof Error && err.message === UNAUTHORIZED_MESSAGE) return { kind: 'signed-out' };

  const name = err instanceof Error || (typeof DOMException !== 'undefined' && err instanceof DOMException)
    ? (err as Error).name
    : '';
  if (name === 'TimeoutError' || name === 'AbortError') {
    return {
      kind: 'unavailable',
      message: `The API didn't answer within ${BOOTSTRAP_TIMEOUT_MS / 1000} seconds. It may be restarting.`,
    };
  }
  // A 502/503 page from the load balancer, or index.html served in place of the
  // API, is HTML: parsing it as JSON throws a SyntaxError.
  if (name === 'SyntaxError') {
    return { kind: 'unavailable', message: 'The API returned a page that is not JSON, usually a load-balancer error while it restarts.' };
  }
  if (name === 'TypeError') {
    return { kind: 'unavailable', message: "Couldn't reach the API. Check your connection." };
  }
  const message = err instanceof Error && err.message ? err.message : 'Unknown error';
  return { kind: 'unavailable', message: `The API couldn't be reached: ${message}` };
}
